import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarIcon, ClipboardCopyIcon, DocumentReportIcon } from "@heroicons/react/outline";
import { formatYmd, parseLooseYmd, todayYmd } from "../../features/cases/dates";

const UPCOMING_DAYS = 14;

// /calendar/small entry types (colors as in MonthlyCalendar).
const KINDS = [
  { id: "astika", label: "Αστικά", types: ["o", "s"], tone: "ok" },
  { id: "dioikitika", label: "Διοικητικά", types: ["a"], tone: "warn" },
  { id: "eauction", label: "Πλειστηριασμοί", types: ["e"], tone: "purple" },
];

const weekdayFormat = new Intl.DateTimeFormat("el-GR", { weekday: "short" });

const ymdToDate = (ymd) =>
  new Date(Math.floor(ymd / 10000), Math.floor((ymd % 10000) / 100) - 1, ymd % 100);

// Next two weeks of the user's calendar, counted per day and case type.
export const UpcomingDatesCard = ({ dates }) => {
  const days = useMemo(() => {
    const today = todayYmd();
    const limitDate = new Date();
    limitDate.setDate(limitDate.getDate() + UPCOMING_DAYS);
    const limit =
      limitDate.getFullYear() * 10000 + (limitDate.getMonth() + 1) * 100 + limitDate.getDate();
    const byDay = new Map();
    dates.forEach((d) => {
      const ymd = parseLooseYmd(d.imerominia);
      if (!ymd || ymd < today || ymd > limit) return;
      const kind = KINDS.find((k) => k.types.includes(d.t));
      if (!kind) return;
      if (!byDay.has(ymd)) byDay.set(ymd, {});
      const counts = byDay.get(ymd);
      counts[kind.id] = (counts[kind.id] ?? 0) + 1;
    });
    return [...byDay.entries()].sort((a, b) => a[0] - b[0]);
  }, [dates]);

  return (
    <section className="ds-card">
      <h2 className="ds-card-title">Επόμενες {UPCOMING_DAYS} ημέρες</h2>
      {days.length === 0 ? (
        <p className="ds-muted ds-card-empty">Δεν υπάρχουν καταχωρημένες ημερομηνίες.</p>
      ) : (
        <ul className="ds-daylist">
          {days.map(([ymd, counts]) => (
            <li key={ymd}>
              <span className="ds-daylist-date">
                <span className="ds-muted">{weekdayFormat.format(ymdToDate(ymd))}</span>{" "}
                {formatYmd(ymd).slice(0, 5)}
              </span>
              <span className="ds-daylist-kinds">
                {KINDS.filter((k) => counts[k.id]).map((k) => (
                  <span key={k.id} className={`ds-badge ds-badge--${k.tone}`}>
                    {counts[k.id]} {k.label}
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}
      <Link to="/calendar" className="ds-card-link">
        Όλο το ημερολόγιο →
      </Link>
    </section>
  );
};

const SyncRow = ({ title, url, note }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error(err);
    }
  };
  return (
    <div className="ds-sync-row">
      <div className="ds-sync-title">{title}</div>
      {note && <div className="ds-muted ds-sync-note">{note}</div>}
      <div className="ds-sync-actions">
        <a className="ds-btn" href={url}>
          <CalendarIcon aria-hidden="true" />
          Συγχρονισμός
        </a>
        <button type="button" className="ds-btn ds-btn--ghost" onClick={copy}>
          <ClipboardCopyIcon aria-hidden="true" />
          {copied ? "Αντιγράφηκε" : "Αντιγραφή συνδέσμου"}
        </button>
      </div>
    </div>
  );
};

// webcal feeds for Apple/Google/Outlook calendars.
export const CalendarSyncCard = ({ calUuid }) => (
  <section className="ds-card">
    <h2 className="ds-card-title">Συγχρονισμός ημερολογίου</h2>
    {calUuid ? (
      <>
        <SyncRow title="Δικάσιμοι" url={`webcal://mysolon.gr/api/webcal/${calUuid}`} />
        <SyncRow
          title="Προθεσμίες"
          url={`webcal://mysolon.gr/api/webcalproth/${calUuid}`}
          note="Οι προθεσμίες υπολογίζονται αυτόματα και είναι ενδεικτικές."
        />
      </>
    ) : (
      <p className="ds-muted ds-card-empty">Φόρτωση…</p>
    )}
  </section>
);

export const ReportCard = ({ reportUUID, reportLoading, onCreate }) => (
  <section className="ds-card">
    <h2 className="ds-card-title">Αναφορά υποθέσεων</h2>
    <p className="ds-muted ds-card-text">
      Όλες οι υποθέσεις σας σε ένα έγγραφο Word.
    </p>
    {reportUUID ? (
      <a className="ds-btn ds-btn--primary" href={`/api/public/${reportUUID}/mysolon-report.docx`}>
        <DocumentReportIcon aria-hidden="true" />
        Λήψη αναφοράς
      </a>
    ) : (
      <button
        type="button"
        className="ds-btn ds-btn--primary"
        onClick={onCreate}
        disabled={reportLoading}
      >
        <DocumentReportIcon aria-hidden="true" />
        {reportLoading ? "Δημιουργία…" : "Δημιουργία αναφοράς σε Word"}
      </button>
    )}
  </section>
);
